
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';

export interface ClientUpcomingBooking {
  id: string;
  booking_date: string;
  time_slot: string;
  workout_type_id: string;
  status: 'confirmed' | 'completed' | 'missed' | 'cancelled';
  booking_type: 'regular' | 'recurring';
  recurring_booking_id?: string;
  workout_type?: {
    id: string;
    name: string;
    color: string;
  };
}

export const useClientUpcomingBookings = (clientId: string, limit?: number) => {
  const { currentOrganization } = useAuth();

  return useQuery({
    queryKey: ['client-upcoming-bookings', clientId, currentOrganization?.id, limit],
    queryFn: async (): Promise<ClientUpcomingBooking[]> => {
      if (!currentOrganization) return [];

      const today = format(new Date(), 'yyyy-MM-dd');
      const threeMonthsFromNow = format(new Date(Date.now() + 90 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');

      // Fetch regular bookings that don't have session consumption records (within 3 months)
      const { data: regularBookings, error: regularError } = await supabase
        .from('bookings')
        .select(`
          id,
          booking_date,
          time_slot,
          workout_type_id,
          status,
          workout_types!inner (
            id,
            name,
            color
          )
        `)
        .eq('client_id', clientId)
        .eq('organization_id', currentOrganization.id)
        .gte('booking_date', today)
        .lte('booking_date', threeMonthsFromNow)
        .eq('status', 'confirmed')
        .order('booking_date', { ascending: true })
        .order('time_slot', { ascending: true });

      if (regularError) {
        console.error('Error fetching regular bookings:', regularError);
        throw regularError;
      }

      // Filter out regular bookings that have session consumption records
      let filteredRegularBookings = [];
      if (regularBookings && regularBookings.length > 0) {
        const { data: consumedRegularBookings, error: consumptionError } = await supabase
          .from('session_consumptions')
          .select('booking_id')
          .eq('client_id', clientId)
          .eq('organization_id', currentOrganization.id)
          .in('booking_id', regularBookings.map(b => b.id));

        if (consumptionError) {
          console.error('Error fetching session consumptions:', consumptionError);
        }

        const consumedBookingIds = new Set(consumedRegularBookings?.map(c => c.booking_id) || []);
        filteredRegularBookings = regularBookings.filter(booking => !consumedBookingIds.has(booking.id));
      }

      // Fetch active recurring bookings for this client that are active or will start within 3 months
      console.log('Fetching recurring bookings for client:', clientId, 'organization:', currentOrganization.id, 'today:', today);
      
      const { data: recurringBookings, error: recurringError } = await supabase
        .from('recurring_bookings')
        .select(`
          id,
          day_of_week,
          time_slot,
          workout_type_id,
          start_date,
          end_date,
          workout_types!inner (
            id,
            name,
            color
          )
        `)
        .eq('client_id', clientId)
        .eq('organization_id', currentOrganization.id)
        .eq('is_active', true)
        .lte('start_date', threeMonthsFromNow); // Show bookings that start within our 3-month window

      console.log('Recurring bookings query result:', {
        data: recurringBookings,
        error: recurringError,
        queryParams: {
          client_id: clientId,
          organization_id: currentOrganization.id,
          is_active: true,
          start_date_lte: threeMonthsFromNow
        }
      });

      if (recurringError) {
        console.error('Error fetching recurring bookings:', recurringError);
      }

      // Fetch session consumptions for recurring bookings
      const { data: recurringConsumptions, error: recurringConsumptionError } = await supabase
        .from('session_consumptions')
        .select('recurring_booking_id, consumption_date')
        .eq('client_id', clientId)
        .eq('organization_id', currentOrganization.id)
        .not('recurring_booking_id', 'is', null);

      if (recurringConsumptionError) {
        console.error('Error fetching recurring session consumptions:', recurringConsumptionError);
      }

      // Fetch booking exceptions for recurring bookings to exclude cancelled dates
      const { data: bookingExceptions, error: exceptionsError } = await supabase
        .from('booking_exceptions')
        .select('recurring_booking_id, exception_date')
        .eq('organization_id', currentOrganization.id);

      if (exceptionsError) {
        console.error('Error fetching booking exceptions:', exceptionsError);
      }

      // Create a set of consumed recurring booking dates for efficient lookup
      const consumedRecurringDates = new Set<string>();
      if (recurringConsumptions) {
        recurringConsumptions.forEach(consumption => {
          if (consumption.recurring_booking_id && consumption.consumption_date) {
            consumedRecurringDates.add(`${consumption.recurring_booking_id}-${consumption.consumption_date}`);
          }
        });
      }

      // Create a set of excepted recurring booking dates for efficient lookup
      const exceptedRecurringDates = new Set<string>();
      if (bookingExceptions) {
        bookingExceptions.forEach(exception => {
          if (exception.recurring_booking_id && exception.exception_date) {
            exceptedRecurringDates.add(`${exception.recurring_booking_id}-${exception.exception_date}`);
          }
        });
      }

      // Combine and format both types of bookings
      const allBookings: ClientUpcomingBooking[] = [];

      // Add filtered regular bookings
      if (filteredRegularBookings) {
        filteredRegularBookings.forEach(booking => {
          allBookings.push({
            id: booking.id,
            booking_date: booking.booking_date,
            time_slot: booking.time_slot,
            workout_type_id: booking.workout_type_id,
            status: booking.status as 'confirmed' | 'completed' | 'missed' | 'cancelled',
            booking_type: 'regular',
            workout_type: {
              id: booking.workout_types.id,
              name: booking.workout_types.name,
              color: booking.workout_types.color,
            }
          });
        });
      }

      // Add recurring bookings for the next 3 months, excluding those with session consumptions
      if (recurringBookings) {
        const currentDate = new Date();
        // Reset time to start of day to avoid timezone issues
        currentDate.setHours(0, 0, 0, 0);
        const endDate = new Date(currentDate.getTime() + 90 * 24 * 60 * 60 * 1000);
        
        recurringBookings.forEach(recurringBooking => {
          // Check if booking is still active (not ended)
          if (recurringBooking.end_date && recurringBooking.end_date < today) {
            return;
          }

          // Generate dates for the next 3 months
          const loopDate = new Date(currentDate);
          for (let i = 0; i <= 90; i++) {
            const dayName = format(loopDate, 'EEEE').toLowerCase();
            const dateStr = format(loopDate, 'yyyy-MM-dd');
            
            if (dayName === recurringBooking.day_of_week && dateStr >= today && dateStr >= recurringBooking.start_date) {
              // Check if booking hasn't ended
              if (!recurringBooking.end_date || dateStr <= recurringBooking.end_date) {
                // Skip if this recurring booking date has already been consumed
                const consumptionKey = `${recurringBooking.id}-${dateStr}`;
                // Skip if this recurring booking date has an exception (cancelled)
                const exceptionKey = `${recurringBooking.id}-${dateStr}`;
                if (!consumedRecurringDates.has(consumptionKey) && !exceptedRecurringDates.has(exceptionKey)) {
                  allBookings.push({
                    id: `recurring-${recurringBooking.id}-${dateStr}`,
                    booking_date: dateStr,
                    time_slot: recurringBooking.time_slot,
                    workout_type_id: recurringBooking.workout_type_id,
                    status: 'confirmed',
                    booking_type: 'recurring',
                    recurring_booking_id: recurringBooking.id,
                    workout_type: {
                      id: recurringBooking.workout_types.id,
                      name: recurringBooking.workout_types.name,
                      color: recurringBooking.workout_types.color,
                    }
                  });
                }
              }
            }
            
            // Move to next day
            loopDate.setDate(loopDate.getDate() + 1);
          }
        });
      }

      // Sort all bookings by date and time
      allBookings.sort((a, b) => {
        const dateComparison = a.booking_date.localeCompare(b.booking_date);
        if (dateComparison !== 0) return dateComparison;
        return a.time_slot.localeCompare(b.time_slot);
      });

      const finalResult = limit ? allBookings.slice(0, limit) : allBookings;

      console.log('useClientUpcomingBookings Debug:', {
        clientId,
        limit,
        totalBookings: allBookings.length,
        finalResultLength: finalResult.length,
        recurringBookingsCount: recurringBookings?.length || 0,
        regularBookingsCount: filteredRegularBookings?.length || 0,
        sampleBookings: finalResult.slice(0, 2).map(b => ({
          id: b.id,
          booking_date: b.booking_date,
          time_slot: b.time_slot,
          booking_type: b.booking_type,
          workout_type_name: b.workout_type?.name
        }))
      });

      return finalResult;
    },
    enabled: !!currentOrganization?.id && !!clientId,
  });
};
